import { useState } from 'react';
import { Account } from 'shared/types/account';
import Checkbox from 'components/gruenland/atoms/checkbox';
import Input, { InputProps } from 'components/gruenland/atoms/input';
import { useFormat } from 'helpers/hooks/useFormat';
import { validateEmail, validateTextExists } from 'helpers/validation';
import { UpdateAccount } from 'frontastic/hooks/useAccount/types';
import AccountForm from '../../../account-atoms/account-form';
import useDiscardForm from '../../../hooks/useDiscardForm';
import useFeedbackToasts from '../../../hooks/useFeedbackToasts';

type inputNameType = 'firstName' | 'lastName' | 'email';

interface Props {
  account?: Account;
  update?: (payload: UpdateAccount) => Promise<Account>;
}
const PersonalInfoForm = ({ account, update }: Props) => {
  const { discardForm } = useDiscardForm();

  const [data, setData] = useState<Account>(account as Account);
  const [loading, setLoading] = useState(false);

  const { notifyDataUpdated, notifyWentWrong } = useFeedbackToasts();

  const { formatMessage } = useFormat({ name: 'gruenland' });

  const invalidNameErrorMessage = formatMessage({
    id: 'validation.name',
    defaultMessage: 'Il nome deve essere di almeno due caratteri.',
  });
  const invalidEmailErrorMessage = formatMessage({ id: 'validation.email', defaultMessage: 'Email non valida.' });

  //input change handler
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  //handle consent checkbox change
  const handleConsentChange = ({ name, checked }: { name: string; checked: boolean }) => {
    setData({ ...data, [name]: checked });
  };

  const handleSubmit = () => {
    setLoading(true);

    update?.({ ...data, email: data.email && data.email !== account?.email ? data.email : undefined })
      .then(() => notifyDataUpdated())
      .then(() => discardForm())
      .then(() => setLoading(false))
      .catch(() => notifyWentWrong());
  };

  const inputFields: Array<InputProps> = [
    {
      label: formatMessage({ id: 'user.firstName', defaultMessage: 'Nome' }),
      name: 'firstName',
      errorMessage: invalidNameErrorMessage,
      validation: validateTextExists,
    },
    {
      label: formatMessage({ id: 'user.lastName', defaultMessage: 'Cognome' }),
      name: 'lastName',
      errorMessage: invalidNameErrorMessage,
      validation: validateTextExists,
    },
    {
      label: formatMessage({ id: 'user.email', defaultMessage: 'Email' }),
      name: 'email',
      errorMessage: invalidEmailErrorMessage,
      validation: validateEmail,
    },
  ];

  return (
    <AccountForm
      title={formatMessage({ id: 'user.editPersonalInfo', defaultMessage: 'Modifica informazioni personali' })}
      requiredLabelIsVisible
      defaultCTASection
      loading={loading}
      onSubmit={handleSubmit}
    >
      <div className="grid gap-3">
        {inputFields.map((fieldProps, index) => (
          <Input
            key={index}
            {...fieldProps}
            onChange={handleChange}
            value={data?.[fieldProps.name as inputNameType] ?? ''}
            required
          />
        ))}

        <div className="mt-5 flex flex-col gap-2">
          <Checkbox
            checked={data.consentTerms}
            label={formatMessage({
              id: 'profile.consentTerms',
              defaultMessage: 'Acconsento ai termini e condizioni',
            })}
            labelPosition="on-right"
            disabled
          />
          <Checkbox
            name="consentProfiling"
            checked={data.consentProfiling}
            onChange={handleConsentChange}
            label={formatMessage({
              id: 'profile.consentProfiling',
              defaultMessage: 'Acconsento al trattamento dei miei dati personali per finalità di profilazione',
            })}
            labelPosition="on-right"
          />
          <Checkbox
            name="consentMarketing"
            id="consentMarketing"
            checked={data.consentMarketing}
            onChange={handleConsentChange}
            label={formatMessage({
              id: 'profile.consentMarketing',
              defaultMessage: 'Acconsento al trattamento dei miei dati personali per finalità di marketing',
            })}
            labelPosition="on-right"
          />
        </div>
      </div>
    </AccountForm>
  );
};

export default PersonalInfoForm;
